// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import { ResponseMessageSchema, StatusSchema } from "./utilitySchemas";
import { UserSchema } from "./userSchemas";

export const NotificationTypeSchema = z.enum([
    "REQUEST_ASSIGNED",
    "REMINDER_ASSIGNED",
    "COMMENT_MENTION",
    "REQUEST_USER_REMARK",
    "REQUEST_COMMENT",
    "REQUEST_ACCOUNT_OWNER_ASSIGNED",
    "REQUEST_ORDERED",
    "REQUEST_CANCELLED",
    "REQUEST_SCHEDULED",
]);

export type NotificationType = z.infer<typeof NotificationTypeSchema>;

export const NotificationCommentScopeSchema = z.enum([
    "REQUEST",
    "ENTITY",
    "USER",
]);

export type NotificationCommentScope = z.infer<
    typeof NotificationCommentScopeSchema
>;

export const RequestAssignedNotificationMetaSchema = z.object({
    scope: z.literal("REQUEST_ASSIGNED"),
    scopeId: z.string(),
    requestEvalionId: z.string(),
    isExternal: z.boolean()
});

export type  RequestAssignedNotificationMeta = z.infer<typeof RequestAssignedNotificationMetaSchema>;

export const ReminderAssignedNotificationMetaSchema = z.object({
    scope: z.literal("REMINDER_ASSIGNED"),
    scopeId: z.string(),
    reminderDescription: z.string(),
});

export type  ReminderAssignedNotificationMeta = z.infer<typeof ReminderAssignedNotificationMetaSchema>;

export const CommentMentionNotificationMetaSchema = z.object({
    scope: z.literal("COMMENT_MENTION"),
    scopeId: z.string(),
    requestId: z.string(),
    requestEvalionId: z.string(),
    isExternal: z.boolean(),
    comment:z.string()
});

export type  CommentMentionNotificationMeta = z.infer<typeof CommentMentionNotificationMetaSchema>;

export const RequestUserRemarkNotificationMetaSchema = z.object({
    scope: z.literal("REQUEST_USER_REMARK"),
    scopeId: z.string(),
    requestId: z.string(),
    requestEvalionId: z.string(),
    isExternal: z.boolean()
});

export type RequestUserRemarkNotificationMeta = z.infer<typeof RequestUserRemarkNotificationMetaSchema>;

export const RequestCommentNotificationMetaSchema = z.object({
    scope: z.literal("REQUEST_COMMENT"),
    scopeId: z.string(),
    requestId: z.string(),
    requestEvalionId: z.string(),
    isExternal: z.boolean(),
    commentId: z.string(),
    comment: z.string(),
});

export type RequestCommentNotificationMeta = z.infer<
    typeof RequestCommentNotificationMetaSchema
>;

export const RequestAccountOwnerAssignedNotificationMetaSchema = z.object({
    scope: z.literal("REQUEST_ACCOUNT_OWNER_ASSIGNED"),
    scopeId: z.string(),
    requestId: z.string(),
    requestEvalionId: z.string(),
    propertyAddress:z.string(),
    isExternal: z.boolean(),
});

export type RequestAccountOwnerAssignedNotificationMeta = z.infer<
    typeof RequestAccountOwnerAssignedNotificationMetaSchema
>;

export const RequestOrderedNotificationMetaSchema = z.object({
    scope: z.literal("REQUEST_ORDERED"),
    scopeId: z.string(),
    requestId: z.string(),
    requestEvalionId: z.string(),
    isExternal: z.boolean(),
});

export type RequestOrderedNotificationMeta = z.infer<
    typeof RequestOrderedNotificationMetaSchema
>;

export const RequestCancelledNotificationMetaSchema = z.object({
    scope: z.literal("REQUEST_CANCELLED"),
    scopeId: z.string(),
    requestId: z.string(),
    requestEvalionId: z.string(),
    isExternal: z.boolean(),
});

export type RequestCancelledNotificationMeta = z.infer<
    typeof RequestCancelledNotificationMetaSchema
>;

export const RequestScheduledNotificationMetaSchema = z.object({
    scope: z.literal("REQUEST_SCHEDULED"),
    scopeId: z.string(),
    requestId: z.string(),
    requestEvalionId: z.string(),
    isExternal: z.boolean(),
    scheduledFor: z.string().nullable(),
});

export type RequestScheduledNotificationMeta = z.infer<
    typeof RequestScheduledNotificationMetaSchema
>;

export const NotificationScopeMetaInfoSchema = z.discriminatedUnion("scope", [
    RequestAssignedNotificationMetaSchema,
    ReminderAssignedNotificationMetaSchema,
    CommentMentionNotificationMetaSchema,
    RequestUserRemarkNotificationMetaSchema,
    RequestCommentNotificationMetaSchema,
    RequestAccountOwnerAssignedNotificationMetaSchema,
    RequestOrderedNotificationMetaSchema,
    RequestCancelledNotificationMetaSchema,
    RequestScheduledNotificationMetaSchema,
]);

export type  NotificationScopeMetaInfo = z.infer<typeof NotificationScopeMetaInfoSchema>;

export const NotificationListItemSchema = z.object({
    id: z.string().uuid(),
    type: NotificationTypeSchema,
    createdAt: z.string().refine((date) => !Number.isNaN(Date.parse(date)), {
        message: "Invalid date format",
    }),
    isRead: z.boolean(),
    actor: UserSchema.pick({
        id: true,
        firstName: true,
        lastName: true,
    }).nullable(),
    requestId: z.string().uuid().nullable(),
    tempRequestId: z.string().uuid().nullable(),
    requestEvalionId: z.string().nullable(),
    reminderId: z.string().uuid().nullable(),
    reminderDescription: z.string().nullable(),
    commentId: z.string().uuid().nullable(),
    commentScope: NotificationCommentScopeSchema.nullable(),
    commentScopeId: z.string().nullable(),
    entityId: z.number().int().positive().nullable(),
    entityName: z.string().nullable(),
    subjectUserId: z.number().int().positive().nullable(),
    subjectUserName: z.string().nullable(),
    isExternal: z.boolean(),
    notificationMeta: NotificationScopeMetaInfoSchema
});

export type NotificationListItem = z.infer<typeof NotificationListItemSchema>;

export const NotificationsQuerySchema = z.object({
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    types: z
        .preprocess((value) => {
            if (typeof value === "string") {
                return value
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(NotificationTypeSchema).optional())
        .optional(),
});

export type NotificationsQuery = z.infer<typeof NotificationsQuerySchema>;

export const NotificationsResponseSchema = z.object({
    status: StatusSchema,
    message: ResponseMessageSchema.optional(),
    data: z.object({
        notifications: z.array(NotificationListItemSchema),
    }),
    pagination: z
        .object({
            total: z.number().int().nonnegative(),
            limit: z.number().int().positive(),
            offset: z.number().int().nonnegative(),
            hasMore: z.boolean(),
        })
        .optional(),
});

export type NotificationsResponse = z.infer<typeof NotificationsResponseSchema>;

export const NotificationReadStatePayloadSchema = z.object({
    lastReadAt: z.string().optional(),
});

export type NotificationReadStatePayload = z.infer<
    typeof NotificationReadStatePayloadSchema
>;

export const NotificationReadStateResponseSchema = z.object({
    status: StatusSchema,
    message: ResponseMessageSchema.optional(),
    data: z.object({
        lastReadAt: z.string().nullable(),
        totalCount: z.number().int().nonnegative(),
        unreadCount: z.number().int().nonnegative(),
    }),
});

export type NotificationReadStateResponse = z.infer<
    typeof NotificationReadStateResponseSchema
>;

export const NotificationItemReadStatePayloadSchema = z.object({
    read: z.boolean(),
});

export type NotificationItemReadStatePayload = z.infer<
    typeof NotificationItemReadStatePayloadSchema
>;
